import { LoadingOutlined } from '@ant-design/icons';
import { Button, Progress, Select, Space } from 'antd';
import FormattedTime from './ReactPlayerControls/FormattedTime';
import Slider, { Direction } from './ReactPlayerControls/Slider';
import { StyledPlayerControls } from './PlayerControls.styled';
import usePlayerControls from './usePlayerControls.hook';
import { useRef } from 'react';
import GamificationScoreBoard from '@/shared/StudentPage/StudentPageTopbar/GamificationScoreBoard/GamificationScoreBoard';
import { ExitFullscreen, Fullscreen } from '@styled-icons/boxicons-regular';
import ExpandedScreenButton from '@/shared/Activities/ActivityComponents/ExpandedScreenButton/ExpandedScreenButton';
import { comicPlayerIcons } from '../ComicPlayer/ComicPlayer';

export default function PlayerControls({
  ready,
  playing,
  bufferTime,
  currentTime,
  duration,
  fullScreen,
  onPlay,
  onPause,
  onSeek,
  noSeek,
  onFullScreen,
  cues,
  showCues,
  currentCue,
  theme,
  videoUrls,
  onVideoUrlChange,
  showPlaybackRate,
  canIncreasePlaybackRate,
  onChangePlaybackRate,
}) {
  const selectRef = useRef();
  const playbackRateSelectRef = useRef();
  const { state, actions } = usePlayerControls({
    videoUrls,
    onVideoUrlChange,
    selectRef,
  });

  return (
    <StyledPlayerControls
      className={`player-controls ${noSeek ? '' : 'can-seek'} ${
        fullScreen ? 'fullscreen-controls' : ''
      }`}
      theme={theme}
    >
      <Button
        className="player-button play-pause"
        size="small"
        onClick={() => {
          ready && (playing ? onPause() : onPlay());
        }}
        icon={
          ready || !videoUrls?.length ? (
            playing ? (
              comicPlayerIcons.pause
            ) : (
              comicPlayerIcons.play
            )
          ) : (
            <LoadingOutlined />
          )
        }
      ></Button>
      <div className="controls-wrapper">
        <Slider
          className="progress-bar"
          direction={Direction.HORIZONTAL}
          onChange={(fraction) => onSeek(duration * fraction)}
        >
          {showCues && cues && duration ? (
            <div className="cue-points">
              {cues.map((point) => {
                return (
                  <div
                    key={point.id}
                    className={`point${
                      currentCue == point.id ? ' current' : ''
                    }`}
                    style={{
                      left: `${((100 * point.time) / duration).toFixed(2)}%`,
                    }}
                  ></div>
                );
              })}
            </div>
          ) : null}
          <Progress
            percent={(100 * bufferTime) / duration}
            success={{
              percent: (100 * currentTime) / duration,
            }}
            showInfo={false}
          />
        </Slider>

        <div className="other-controls">
          {fullScreen && <GamificationScoreBoard />}
          <div className="time">
            <Space>
              <FormattedTime className="current" numSeconds={currentTime} /> /
              <FormattedTime className="duration" numSeconds={duration} />
            </Space>
          </div>

          {videoUrls?.length > 1 ? (
            <div>
              <Select
                ref={selectRef}
                size="small"
                value={state.videoUrlIndex}
                onSelect={actions.setVideoUrlIndex}
                className="height-select"
                placement="topLeft"
                dropdownMatchSelectWidth={false}
                getPopupContainer={() =>
                  document.querySelector('.player-controls')
                }
              >
                {videoUrls.map((videoUrl, index) => (
                  <Select.Option value={index} key={index}>
                    {getVideoQualityText(videoUrl.height)}
                  </Select.Option>
                ))}
              </Select>
            </div>
          ) : null}
          {showPlaybackRate && (
            <>
              <Select
                size="small"
                className="playback-rate-select"
                ref={playbackRateSelectRef}
                defaultValue={1}
                value={state.playbackRate}
                onSelect={onChangePlaybackRate}
                placement="topLeft"
                dropdownMatchSelectWidth={false}
                getPopupContainer={() =>
                  document.querySelector('.player-controls')
                }
              >
                {(canIncreasePlaybackRate
                  ? [0.75, 1, 1.25, 1.5]
                  : [0.75, 1]
                ).map((rate, index) => (
                  <Select.Option value={rate} key={index}>
                    {rate} x
                  </Select.Option>
                ))}
              </Select>
            </>
          )}
        </div>
      </div>
      {fullScreen ? null : <ExpandedScreenButton className="player-button" />}
      {onFullScreen && (
        <Button
          className="player-button fullscreen-button"
          size="small"
          onClick={onFullScreen}
        >
          {fullScreen ? <ExitFullscreen size={20} /> : <Fullscreen size={18} />}
        </Button>
      )}
    </StyledPlayerControls>
  );
}

function getVideoQualityText(height) {
  return (
    {
      1080: 'Full HD',
      720: 'HD',
      480: 'SD',
      360: 'Low',
      224: 'Lowest',
    }[height] || `${height}p`
  );
}
